/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/dvrdOptionsList.scss';

import {AwesomeIcon, convertColor, directTimeout, editColor, hasHover} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {CSSProperties, MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {ControlContext} from "../util/controlContext";

type Option = {
    label: React.ReactNode;
    onClick: MouseEventHandler;
    hoverColor?: 'default' | 'red';
}

interface Props {
    options: Option[];
    label: React.ReactNode;
    className?: string;
    disabled?: boolean;
    hoverOpen?: boolean;
}

export default function DvrdOptionsList(props: Props) {
    const context = useContext(ControlContext),
        {className, label, options, disabled, hoverOpen} = props,
        [open, setOpen] = useState(false),
        button = useRef<HTMLDivElement>(null),
        [hover, setHover] = useState(false);

    function onClickButton() {
        if (!hoverOpen) setOpen(true);
    }

    function onMouseEnter() {
        setHover(true);
    }

    function onMouseLeave() {
        setHover(false);
    }

    function getButtonStyle(): CSSProperties {
        return {
            backgroundColor: getBackgroundColor(),
            color: getColor(),
        }
    }

    function getBackgroundColor(): string {
        let color: string;
        if (disabled) color = 'color-gray-4';
        else color = context.baseColor;
        color = convertColor(color);
        if (hover) color = editColor(-.2, color);
        return color;
    }

    function getColor(): string {
        let color: string;
        if (disabled) color = 'color-gray-3';
        else color = context.contrastColor;
        return convertColor(color);
    }

    function clickListener() {
        directTimeout(() => {
            if (!hasHover(button.current)) setOpen(false);
        });
    }

    useEffect(() => {
        if (open)
            document.addEventListener('click', clickListener);
        else
            document.removeEventListener('click', clickListener);
        return function () {
            document.removeEventListener('click', clickListener);
        }
    }, [open]);

    function renderLabel(label: React.ReactNode, className: string) {
        if (typeof label === 'string') return <label className={className}>{label}</label>;
        return label;
    }

    function renderOptions() {
        return (
            <div className='options-container'>
                {options.map((option: Option, index: number) => (
                    <div key={index} className={classNames('option', option.hoverColor ?? 'default')}
                         onClick={disabled ? undefined : option.onClick}>
                        {renderLabel(option.label, 'option-label')}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div
            className={classNames('dvrd-options-list', open && 'open', disabled && 'disabled', className, hoverOpen && 'hover')}>
            <div className={classNames('btn-container')} ref={button} onClick={onClickButton} style={getButtonStyle()}
                 onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                <AwesomeIcon name='bars' className='caret'/>
                {renderLabel(label, 'button-label')}
            </div>
            {renderOptions()}
        </div>
    )
}